import { Logout } from "@mui/icons-material";
import { Box, Container, Divider, Drawer, Link, List, ListItem, ListItemButton, ListItemIcon, ListItemText, styled, useMediaQuery, useTheme } from "@mui/material";
import CssBaseline from '@mui/material/CssBaseline';
import { FC, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, RouteProps, useLocation } from "react-router-dom";
import Logo from "../assets/beetogether-logo-520x520.png";
import Footer from "../common/components/Footer";
import Header from "../common/components/Header";
import { useAuth } from "../common/hooks/useAuth";
import { navOptionsRoutes } from "./Routes";

export const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    flexGrow: 1,
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
    }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));


interface IRedirectProps { }

const Redirect: FC<IRedirectProps> = () => {
    const [open, setOpen] = useState(true);
    const [openMobile, setOpenMobile] = useState(false);
    const { logout, permissionsData } = useAuth();
    const location = useLocation();
    const { t } = useTranslation();

    const theme = useTheme();
    const largeScreen = useMediaQuery(theme.breakpoints.up("lg"));

    const getNavOptions = () => {
        let showRoutes: (RouteProps & {
            label: string;
            childrenPaths?: RouteProps[];
            icon?: ReactNode;
            permissions: string[];
        })[] = []

        const unprotectedRoutes = navOptionsRoutes.filter(option => option.permissions.length === 0)

        if (permissionsData) {
            Object.keys(permissionsData).map(node => {
                permissionsData[node].map(perm => {
                    showRoutes = [
                        ...showRoutes,
                        ...navOptionsRoutes.filter(
                            option => option.permissions.length === 1 && perm.includes(option.permissions[0])
                                ||
                                option.permissions.length > 1 &&
                                perm.includes(option.permissions[0]) &&
                                Object.keys(permissionsData).includes(option.permissions[1].split("_")[0]) &&
                                permissionsData[option.permissions[1].split("_")[0]].find(p => p.includes(option.permissions[1]))
                        )
                    ]
                })
            })
        }
        // remove double elements and append unprotected routes
        showRoutes = [
            ...showRoutes.filter((value, index, self) => self.indexOf(value) === index),
            ...unprotectedRoutes
        ]

        return showRoutes;
    }

    const handleDrawerMobileClose = () => {
        setOpenMobile(false);
    };

    const toggleMenuMobile = (value: boolean) => {
        setOpenMobile(value)
    }

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const toggleMenu = (value: boolean) => {
        setOpen(value)
    }

    return largeScreen ? <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <Drawer
            open={open}
            onClose={handleDrawerClose}
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                '&.MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                },
            }}
            variant="persistent"
            anchor="left"
        >
            <Box width={drawerWidth} height="100%" role="presentation">
                <DrawerHeader>
                    <Box
                        component={Link}
                        href="/"
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            flexGrow: 1,
                            p: 2
                        }}
                    >
                        <Box
                            component="img"
                            src={Logo}
                            sx={{ width: 128 }}
                        >
                        </Box>
                    </Box>
                </DrawerHeader>
                <Divider />
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "calc(100% - 162px)"
                }}>
                    <List>
                        {
                            getNavOptions().map((route) => (
                                <ListItem key={route.path} disablePadding>
                                    <ListItemButton color='primary'
                                        selected={route.path && location.pathname.includes(route.path) ? true : false}
                                        component={Link}
                                        href={route.path}
                                        sx={{
                                            fontWeight: route.path && location.pathname.includes(route.path) ? 600 : 400,
                                            color: route.path && location.pathname.includes(route.path) ? "primary" : "default"
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            {route.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={
                                            t(route.label)
                                        } />
                                    </ListItemButton>
                                </ListItem>
                            ))
                        }
                    </List>
                    <Box>
                        <Divider />
                        <ListItem disablePadding>
                            <ListItemButton
                                color='primary'
                                onClick={() => logout()}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Logout />
                                </ListItemIcon>
                                <ListItemText primary={t("sidebar.logout")} />
                            </ListItemButton>
                        </ListItem>
                    </Box>
                </Box>
            </Box>
        </Drawer>
        <Box sx={{ width: "100%" }}>
            <Main open={open}>
                <Header handleDrawer={toggleMenu} open={open} />
                <Container
                    maxWidth={false}
                    sx={{
                        minHeight: {
                            md: "calc(100vh - 140px)",
                            sm: "calc(100vh - 134px)",
                            xs: "calc(100vh - 118px)",
                        },
                        p: 2
                    }}>
                    <Outlet />
                </Container>
                <Footer />
            </Main>
        </Box>
    </Box> : <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <Drawer
            open={openMobile}
            onClose={handleDrawerMobileClose}
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                '&.MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                },
            }}
            variant="temporary"
            ModalProps={{
                keepMounted: false,
            }}
        >
            <Box width={drawerWidth} height="100%" role="presentation">
                <DrawerHeader>
                    <Box
                        component={Link}
                        href="/"
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            flexGrow: 1,
                            p: 2
                        }}
                    >
                        <Box
                            component="img"
                            src={Logo}
                            sx={{ width: 128 }}
                        >
                        </Box>
                    </Box>
                </DrawerHeader>
                <Divider />
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "calc(100% - 162px)"
                }}>
                    <List>
                        {
                            getNavOptions().map((route) => (
                                <ListItem key={route.path} disablePadding>
                                    <ListItemButton color='primary'
                                        selected={route.path && location.pathname.includes(route.path) ? true : false}
                                        component={Link}
                                        href={route.path}
                                        sx={{
                                            fontWeight: route.path && location.pathname.includes(route.path) ? 600 : 400,
                                            color: route.path && location.pathname.includes(route.path) ? "primary" : "default"
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            {route.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={
                                            t(route.label)
                                        } />
                                    </ListItemButton>
                                </ListItem>
                            ))
                        }
                    </List>
                    <Box>
                        <Divider />
                        <ListItem disablePadding>
                            <ListItemButton
                                color='primary'
                                onClick={() => logout()}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Logout />
                                </ListItemIcon>
                                <ListItemText primary={t("sidebar.logout")} />
                            </ListItemButton>
                        </ListItem>
                    </Box>
                </Box>
            </Box>
        </Drawer>
        <Box sx={{ width: "100%" }}>
            <Header handleDrawer={toggleMenuMobile} open={openMobile} />
            <Container
                maxWidth={false}
                sx={{
                    minHeight: {
                        md: "calc(100vh - 140px)",
                        sm: "calc(100vh - 134px)",
                        xs: "calc(100vh - 118px)",
                    },
                    p: 2
                }}>
                <Outlet />
            </Container>
            <Footer />
        </Box>
    </Box>
}

export default Redirect;